import { Activity, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  AdminStatusPill,
  EmptyState,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonCardGrid,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ADMIN_ACTION_BUTTON } from '@/shared/ui-class';
import { SEED_SERVICE_HEALTH_ROWS } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  detailChips,
  formatRelative,
  healthSummary,
  keyStatusLabel,
  mockRefreshServices,
  mockTestService,
  orderedRows,
  PROVIDER_LABELS,
  PROBE_STALE_MS,
  SERVICE_TO_PROVIDER,
  statusRail,
} from './ServiceHealthScene.transform';

export function ServiceHealthScene() {
  const [services, setServices] = useState(SEED_SERVICE_HEALTH_ROWS);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState<
    (typeof SERVICE_TO_PROVIDER)[keyof typeof SERVICE_TO_PROVIDER] | null
  >(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const loadServices = useCallback(() => {
    setError(null);
    setServices(SEED_SERVICE_HEALTH_ROWS);
  }, []);

  const handleRefreshAll = useCallback(() => {
    setRefreshing(true);
    setRowError(null);
    setServices((prev) => mockRefreshServices(prev));
    setRefreshing(false);
  }, []);

  const handleTestNow = useCallback(
    (service: keyof typeof SERVICE_TO_PROVIDER) => {
      setTesting(service);
      setRowError(null);
      setServices((prev) => mockTestService(prev, service));
      setTesting(null);
    },
    [],
  );

  const panel = (() => {
    if (loading) {
      return <SkeletonCardGrid count={3} />;
    }

    if (error) {
      return (
        <EmptyState
          icon={<Activity className="h-8 w-8 text-zinc-400" />}
          title="Couldn't load API health"
          description={error}
          action={
            <button
              className={ADMIN_ACTION_BUTTON}
              onClick={() => loadServices()}
              type="button"
            >
              Try again
            </button>
          }
        />
      );
    }

    const rows = orderedRows(services);
    const summary = healthSummary(rows);

    return (
      <div className="space-y-4">
        <AdminBulkActionRow
          left={
            <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
              <span
                aria-hidden
                className={cn('inline-block h-2 w-2 rounded-full', summary.dot)}
              />
              {summary.text}
            </span>
          }
        >
          <button
            type="button"
            onClick={() => handleRefreshAll()}
            disabled={refreshing}
            className={ADMIN_ACTION_BUTTON}
          >
            {refreshing ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                Refreshing…
              </span>
            ) : (
              'Refresh all'
            )}
          </button>
        </AdminBulkActionRow>

        {rowError ? <InlineError>{rowError}</InlineError> : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => {
            const provider = SERVICE_TO_PROVIDER[row.service];
            const chips = detailChips(row);

            return (
              <AdminSectionCard
                key={row.service}
                title={PROVIDER_LABELS[provider]}
                actions={<AdminStatusPill status={row.status} dot />}
                className={cn('border-l-2', statusRail(row.status))}
              >
                <div className="space-y-3">
                  {row.userImpact ? (
                    <p className="text-sm text-muted">{row.userImpact}</p>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-1.5">
                    {row.keyStatus === 'missing' ? (
                      <Pill tone="warning">{keyStatusLabel(row.keyStatus)}</Pill>
                    ) : (
                      <Pill>{keyStatusLabel(row.keyStatus)}</Pill>
                    )}
                    {!row.kitEnabled ? (
                      <Pill tone="warning">Kit disabled</Pill>
                    ) : null}
                  </div>

                  <div className="text-xs text-muted">
                    {row.probe ? (
                      <>
                        <span>Checked {formatRelative(row.probe.checkedAt)}</span>
                        {row.probeStale ? (
                          <span className="ml-1.5 text-amber-700 dark:text-amber-500">
                            · &gt;24h stale
                          </span>
                        ) : null}
                        {row.probe.errorMessage ? (
                          <p className="mt-1 text-red-700 dark:text-red-400">
                            {row.probe.errorMessage}
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <span>Not checked yet — run Test now</span>
                    )}
                  </div>

                  {chips.length > 0 ? (
                    <p className="text-xs text-muted">{chips.join(' · ')}</p>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => handleTestNow(row.service)}
                    disabled={testing === row.service || refreshing}
                    className={ADMIN_ACTION_BUTTON}
                  >
                    {testing === row.service ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        Testing…
                      </span>
                    ) : (
                      'Test now'
                    )}
                  </button>
                </div>
              </AdminSectionCard>
            );
          })}
        </div>

        <p className="text-xs text-muted">
          Probes use the effective key after rotation. Marked stale after{' '}
          {Math.round(PROBE_STALE_MS / 3_600_000)}h without a check.
        </p>
      </div>
    );
  })();

  return (
    <AdminSceneShell
      sceneName="ServiceHealthScene"
      title="API health"
      description="Live status for Daytona, Claude ADK, and Google ADK. Manage keys in Provider keys."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">{panel}</Commentable>
    </AdminSceneShell>
  );
}
